import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import clsx from 'clsx';
import { api } from './api';
import { downloadCsv, PeriodBar, usePeriod } from './SalesPages';
import { Alert, Button, Card } from './ui';

interface Rx {
  id: string; issue_date: string; full_number: string; number: string; rx_date: string; prescriber: string; prescriber_licence: string | null;
  patient: string | null; patient_id: string | null; vias: number; controlled: boolean; store: string; pharmacist: string | null;
  lines: { description: string; quantity: string; controlled: boolean; substitutedFrom: string | null; prescriberConsent: boolean | null }[];
}
const dmy = (d: string) => d.split('-').reverse().join('/');

/**
 * Livros da farmácia (Decreto 16/2023, art. 70): registo geral das receitas
 * aviadas e o de controlo especial. Electrónicos, imprimíveis e exportáveis,
 * até haver o modelo oficial da ANARME.
 */
export function PharmacyPage() {
  const [p, setP] = usePeriod();
  const [book, setBook] = useState<'all' | 'controlled'>('all');
  const [items, setItems] = useState<Rx[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setItems(null);
    api<{ items: Rx[] }>(`/pharmacy/prescriptions?from=${p.from}&to=${p.to}${book === 'controlled' ? '&controlled=1' : ''}`).then((r) => setItems(r.items), (e) => setErr((e as Error).message));
  }, [p.from, p.to, book]);
  const title = book === 'controlled' ? 'Livro de controlo especial' : 'Livro de registo de receitas';
  const exportCsv = () => items && downloadCsv(`${book === 'controlled' ? 'controlo-especial' : 'receitas'}-${p.from}-${p.to}.csv`, [
    [title, `${dmy(p.from)} a ${dmy(p.to)}`], [],
    ['Data', 'Documento', 'Receita', 'Data da receita', 'Prescritor', 'Cédula', 'Doente', 'Documento do doente', 'Vias', 'Medicamento', 'Quantidade', 'Substituiu', 'Consentimento do prescritor', 'Farmacêutico', 'Loja'],
    ...items.flatMap((r) => r.lines.filter((l) => book === 'all' || l.controlled).map((l) => [
      dmy(r.issue_date), r.full_number, r.number, dmy(r.rx_date), r.prescriber, r.prescriber_licence ?? '', r.patient ?? '', r.patient_id ?? '', r.vias,
      l.description, l.quantity, l.substitutedFrom ?? '', l.substitutedFrom ? (l.prescriberConsent ? 'sim' : 'não') : '', r.pharmacist ?? '', r.store,
    ])),
  ]);
  return (
    <div className="max-w-6xl space-y-4">
      <PeriodBar p={p} set={setP}>{items && <Button variant="ghost" onClick={exportCsv}><Download size={16} /> Excel</Button>}</PeriodBar>
      <div role="tablist" className="flex gap-2">
        {([['all', 'Receitas'], ['controlled', 'Controlo especial']] as const).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={book === k} onClick={() => setBook(k)} className={clsx('h-11 rounded-lg px-4 font-medium', book === k ? 'bg-brand-700 text-white' : 'border border-base')}>{l}</button>
        ))}
      </div>
      {err && <Alert>{err}</Alert>}
      <Card title={title}>
        {!items ? <p className="text-muted">A carregar…</p> : !items.length ? <p className="text-muted">Sem receitas aviadas neste período.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted"><tr><th className="py-2">Data</th><th>Receita</th><th>Prescritor</th><th>Doente</th><th>Aviado</th><th>Farmacêutico</th></tr></thead>
              <tbody className="divide-y divide-[var(--border)]">
                {items.map((r) => (
                  <tr key={r.id} className="align-top">
                    <td className="py-2">{dmy(r.issue_date)}<span className="block text-xs text-muted">{r.full_number}</span></td>
                    <td>{r.number}<span className="block text-xs text-muted">{dmy(r.rx_date)} · {r.vias} via(s)</span></td>
                    <td>{r.prescriber}{r.prescriber_licence && <span className="block text-xs text-muted">{r.prescriber_licence}</span>}</td>
                    <td>{r.patient ?? '—'}{r.patient_id && <span className="block text-xs text-muted">{r.patient_id}</span>}</td>
                    <td>{r.lines.filter((l) => book === 'all' || l.controlled).map((l, i) => (
                      <div key={i}>{l.quantity.replace(/\.?0+$/, '')} × {l.description}{l.substitutedFrom && <span className="block text-xs text-muted">em vez de {l.substitutedFrom} ({l.prescriberConsent ? 'com' : 'sem'} consentimento)</span>}</div>
                    ))}</td>
                    <td>{r.pharmacist ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
